import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronRight, ChevronLeft, MessageSquare, BookOpen, Settings, Layout, Sparkles, Video, Languages, Users, Eye, Landmark } from 'lucide-react';
import { GospelLensIcon } from '@/components/GospelLensIcon';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';


const ICON_MAP = {
  message: MessageSquare,
  bible: BookOpen,
  settings: Settings,
  layout: Layout,
  sparkles: Sparkles,
  youtube: Video,
  video: Video,
  languages: Languages,
  users: Users,
  eye: Eye,
  gospel_lens: GospelLensIcon,
  landmark: Landmark,
};

const DEFAULT_COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#22c55e', '#06b6d4', '#ef4444'];

function FeaturePreviewCard({ preview, color, icon, isLight }) {
  if (!preview) return null;
  const lines = preview.split('\n').filter(Boolean);
  return (
    <div
      className="rounded-xl p-3 mb-3 overflow-hidden relative"
      style={{
        background: isLight
          ? `linear-gradient(135deg, ${color}08, ${color}15)`
          : `linear-gradient(135deg, ${color}12, ${color}25)`,
        border: `1px solid ${color}30`,
      }}
    >
      {/* Decorative glow */}
      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-20 blur-xl" style={{ background: color }} />
      <div className="relative space-y-1.5">
        {lines.map((line, i) => {
          if (line.startsWith('>>')) {
            // Simulated AI response
            return (
              <div key={i} className="rounded-lg px-2.5 py-1.5 text-[11px] leading-relaxed" style={{ background: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)', color: isLight ? '#334155' : '#e2e8f0' }}>
                {line.replace('>>', '').trim()}
              </div>
            );
          }
          if (line.startsWith('**')) {
            // Bold label
            return <p key={i} className="text-[11px] font-semibold" style={{ color }}>{line.replace(/\*\*/g, '')}</p>;
          }
          if (line.startsWith('--')) {
            // Divider
            return <div key={i} className="h-px my-1" style={{ background: `${color}20` }} />;
          }
          if (line.startsWith('[x]')) {
            // Checkbox item
            return (
              <div key={i} className="flex items-center gap-2">
                <div className="w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0" style={{ borderColor: color, background: `${color}20` }}>
                  <svg viewBox="0 0 12 12" className="w-2.5 h-2.5" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 6l2.5 2.5 4.5-5" /></svg>
                </div>
                <span className="text-[11px]" style={{ color: isLight ? '#475569' : '#94a3b8' }}>{line.replace('[x]', '').trim()}</span>
              </div>
            );
          }
          if (line.startsWith('[]')) {
            // Toggle item
            return (
              <div key={i} className="flex items-center gap-2">
                <div className="w-7 h-4 rounded-full" style={{ background: `${color}40` }}>
                  <div className="w-3 h-3 rounded-full ml-3.5 mt-0.5" style={{ background: color }} />
                </div>
                <span className="text-[11px]" style={{ color: isLight ? '#475569' : '#94a3b8' }}>{line.replace('[]', '').trim()}</span>
              </div>
            );
          }
          // Normal text
          return <p key={i} className="text-[11px]" style={{ color: isLight ? '#475569' : '#94a3b8' }}>{line}</p>;
        })}
      </div>
    </div>
  );
}

export default function TutorialOverlay({ onComplete }) {
  const [steps, setSteps] = useState([]);
  const [current, setCurrent] = useState(0);
  const [version, setVersion] = useState(1);
  const [targetRect, setTargetRect] = useState(null);
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const overlayRef = useRef(null);
  const { colorMode } = useTheme();
  const { t } = useTranslation();
  const isLight = colorMode === 'light';

  useEffect(() => {
    if (localStorage.getItem('eili_tutorial_done')) return;
    const fetchTutorial = async () => {
      try {
        const res = await axios.get(`${API}/tutorials/active`, { withCredentials: true });
        if (res.data.show && res.data.steps?.length > 0) {
          setSteps(res.data.steps);
          setVersion(res.data.version || 1);
          setTimeout(() => setVisible(true), 800);
        }
      } catch {}
    };
    fetchTutorial();
  }, []);

  // Listen for replay-tutorial event from Settings
  useEffect(() => {
    const handleReplay = async () => {
      try {
        const res = await axios.get(`${API}/tutorials/active`, { withCredentials: true });
        const stepsData = res.data.steps || [];
        if (stepsData.length > 0) {
          setSteps(stepsData);
          setVersion(res.data.version || 1);
          setCurrent(0);
          setTargetRect(null);
          setTimeout(() => setVisible(true), 300);
        }
      } catch {}
    };
    window.addEventListener('replay-tutorial', handleReplay);
    return () => window.removeEventListener('replay-tutorial', handleReplay);
  }, []);

  const measureTarget = useCallback(() => {
    if (steps.length === 0 || current >= steps.length) return;
    const step = steps[current];
    if (step.mode === 'showcase') { setTargetRect(null); return; }

    // Find a visible element matching the selector (skip hidden desktop-only elements)
    let el = document.querySelector(step.target_selector);
    if (el) {
      const rect = el.getBoundingClientRect();
      // If target is hidden (e.g. desktop-only on mobile), try the -mobile variant
      if (rect.width === 0 || rect.height === 0) {
        const mobileSelector = step.target_selector.replace(/"\]$/, '-mobile"]');
        const mobileEl = document.querySelector(mobileSelector);
        if (mobileEl) {
          const mRect = mobileEl.getBoundingClientRect();
          if (mRect.width > 0 && mRect.height > 0) el = mobileEl;
        }
      }
    }

    if (el) {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setTargetRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
      } else {
        setTargetRect(null);
      }
    } else {
      setTargetRect(null);
    }
  }, [steps, current]);

  useEffect(() => {
    measureTarget();
    window.addEventListener('resize', measureTarget);
    window.addEventListener('scroll', measureTarget, true);
    return () => {
      window.removeEventListener('resize', measureTarget);
      window.removeEventListener('scroll', measureTarget, true);
    };
  }, [measureTarget]);

  const finish = async (skipped) => {
    setVisible(false);
    localStorage.setItem('eili_tutorial_done', 'true');
    try {
      await axios.post(`${API}/tutorials/complete`, { skipped, version, last_step: current }, { withCredentials: true });
    } catch {}
    setTimeout(() => { onComplete?.(); setSteps([]); }, 300);
  };

  const goTo = (idx) => {
    setAnimating(true);
    setTimeout(() => { setCurrent(idx); setAnimating(false); }, 150);
  };

  const next = () => { if (current < steps.length - 1) goTo(current + 1); else finish(false); };
  const back = () => { if (current > 0) goTo(current - 1); };

  if (!visible || steps.length === 0) return null;

  const step = steps[current];
  const accentColor = step.highlight_color || DEFAULT_COLORS[current % DEFAULT_COLORS.length];
  const StepIcon = ICON_MAP[step.icon] || Sparkles;
  const isShowcase = step.mode === 'showcase';
  const pad = 10;

  // Tooltip positioning
  const getTooltipStyle = () => {
    if (!targetRect || isShowcase) {
      return { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10002, maxWidth: `min(380px, calc(100vw - 32px))` };
    }
    const style = { position: 'fixed', zIndex: 10002 };
    const cx = targetRect.left + targetRect.width / 2;
    const cy = targetRect.top + targetRect.height / 2;
    const arrowPos = step.arrow_position || 'bottom';
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 16;
    const tooltipW = Math.min(340, vw - margin * 2);
    style.maxWidth = tooltipW;

    // Clamp horizontal center so tooltip stays within viewport
    const clampedCx = Math.max(margin + tooltipW / 2, Math.min(vw - margin - tooltipW / 2, cx));

    if (arrowPos === 'top') {
      const proposedTop = targetRect.top - pad - 14;
      // If not enough space above, flip to bottom
      if (proposedTop < tooltipW * 0.8 + margin) {
        style.top = targetRect.top + targetRect.height + pad + 14; style.left = clampedCx; style.transform = 'translate(-50%, 0)';
      } else {
        style.top = proposedTop; style.left = clampedCx; style.transform = 'translate(-50%, -100%)';
      }
    } else if (arrowPos === 'bottom') {
      const proposedTop = targetRect.top + targetRect.height + pad + 14;
      // If not enough space below, flip to top
      if (proposedTop + 200 > vh) {
        style.top = targetRect.top - pad - 14; style.left = clampedCx; style.transform = 'translate(-50%, -100%)';
      } else {
        style.top = proposedTop; style.left = clampedCx; style.transform = 'translate(-50%, 0)';
      }
    } else if (arrowPos === 'left') {
      style.top = cy; style.left = targetRect.left - pad - 14; style.transform = 'translate(-100%, -50%)';
    } else {
      style.top = cy; style.left = targetRect.left + targetRect.width + pad + 14; style.transform = 'translate(0, -50%)';
    }
    return style;
  };

  // Arrow pointing from tooltip to target
  const Arrow = () => {
    if (!targetRect) return null;
    const arrowPos = step.arrow_position || 'bottom';
    const commonStyle = { position: 'absolute', zIndex: 10003 };
    const bg = isLight ? '#ffffff' : '#1e293b';

    if (arrowPos === 'bottom') return <div style={{ ...commonStyle, top: -8, left: '50%', transform: 'translateX(-50%)' }}><div style={{ width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottom: `8px solid ${bg}` }} /></div>;
    if (arrowPos === 'top') return <div style={{ ...commonStyle, bottom: -8, left: '50%', transform: 'translateX(-50%)' }}><div style={{ width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: `8px solid ${bg}` }} /></div>;
    if (arrowPos === 'left') return <div style={{ ...commonStyle, right: -8, top: '50%', transform: 'translateY(-50%)' }}><div style={{ width: 0, height: 0, borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderLeft: `8px solid ${bg}` }} /></div>;
    return <div style={{ ...commonStyle, left: -8, top: '50%', transform: 'translateY(-50%)' }}><div style={{ width: 0, height: 0, borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderRight: `8px solid ${bg}` }} /></div>;
  };

  const cardBg = isLight ? '#ffffff' : '#1e293b';
  const titleColor = isLight ? '#0f172a' : '#f1f5f9';
  const descColor = isLight ? '#475569' : '#94a3b8';
  const stepLabelColor = isLight ? '#64748b' : '#64748b';
  const btnSecBg = isLight ? '#f1f5f9' : '#334155';
  const btnSecText = isLight ? '#475569' : '#94a3b8';
  const progress = ((current + 1) / steps.length) * 100;

  return (
    <div ref={overlayRef} className="fixed inset-0 z-[10000]" style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.3s ease' }} data-testid="tutorial-overlay">
      {/* Dark overlay with spotlight cutout */}
      <svg className="fixed inset-0 w-full h-full pointer-events-none" style={{ zIndex: 10000 }}>
        <defs>
          <mask id="spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            {targetRect && !isShowcase && (
              <rect
                x={targetRect.left - pad} y={targetRect.top - pad}
                width={targetRect.width + pad * 2} height={targetRect.height + pad * 2}
                rx="14" fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%" height="100%"
          fill={isLight ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.6)'}
          mask="url(#spotlight-mask)"
        />
      </svg>
      {/* Spotlight glow ring */}
      {targetRect && !isShowcase && (
        <div
          className="fixed rounded-2xl pointer-events-none"
          style={{
            zIndex: 10001,
            top: targetRect.top - pad, left: targetRect.left - pad,
            width: targetRect.width + pad * 2, height: targetRect.height + pad * 2,
            boxShadow: `0 0 0 3px ${accentColor}50, 0 0 30px ${accentColor}30`,
            transition: 'all 0.3s ease',
          }}
          data-testid="tutorial-spotlight"
        />
      )}
      {/* Tooltip Card */}
      <div
        style={(() => { const ts = getTooltipStyle(); return { ...ts, minWidth: Math.min(290, window.innerWidth - 32), opacity: animating ? 0 : 1, transform: `${ts.transform || ''} ${animating ? 'scale(0.95)' : 'scale(1)'}`, transition: 'opacity 0.15s ease, transform 0.15s ease' }; })()}
        data-testid="tutorial-tooltip"
      >
        {!isShowcase && <Arrow />}
        <div
          className="rounded-2xl overflow-hidden"
          style={{
            background: cardBg,
            boxShadow: `0 0 0 1px ${accentColor}20, 0 20px 60px rgba(0,0,0,${isLight ? '0.15' : '0.4'}), 0 0 40px ${accentColor}10`,
          }}
        >
          {/* Accent header bar */}
          <div className="h-1" style={{ background: `linear-gradient(90deg, ${accentColor}, ${accentColor}80)` }} />

          <div className="p-5">
            {/* Close */}
            <button
              onClick={() => finish(true)}
              className="absolute top-4 right-4 p-1.5 rounded-full transition-all hover:scale-110"
              style={{ color: stepLabelColor, background: isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)' }}
              data-testid="tutorial-skip-btn"
            >
              <X className="h-3.5 w-3.5" />
            </button>

            {/* Icon + Step label */}
            <div className="flex items-center gap-2.5 mb-3">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${accentColor}20, ${accentColor}35)` }}
              >
                <StepIcon className="h-4.5 w-4.5" style={{ color: accentColor }} />
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: accentColor }}>
                  {t('generated.tutorialoverlay_step_b0ce')} {current + 1} of {steps.length}
                </span>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-[15px] font-bold mb-1.5 leading-snug" style={{ color: titleColor }}>{step.title}</h3>

            {/* Description */}
            <p className="text-xs leading-relaxed mb-3" style={{ color: descColor }}>{step.description}</p>

            {/* Feature Preview (showcase mode) */}
            {isShowcase && step.feature_preview && (
              <FeaturePreviewCard preview={step.feature_preview} color={accentColor} icon={step.icon} isLight={isLight} />
            )}

            {/* Progress bar */}
            <div className="h-1 rounded-full mb-4 overflow-hidden" style={{ background: isLight ? '#e2e8f0' : '#334155' }}>
              <div className="h-full rounded-full transition-all duration-300" style={{ width: `${progress}%`, background: `linear-gradient(90deg, ${accentColor}, ${accentColor}cc)` }} />
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-2">
              {current > 0 && (
                <button
                  onClick={back}
                  className="flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-medium transition-all hover:brightness-95"
                  style={{ background: btnSecBg, color: btnSecText }}
                  data-testid="tutorial-back-btn"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  {t('tutorial.back', 'Back')}
                </button>
              )}
              {current === 0 && (
                <button
                  onClick={() => finish(true)}
                  className="px-3 py-2 rounded-xl text-xs font-medium transition-all"
                  style={{ color: stepLabelColor }}
                  data-testid="tutorial-skip-all-btn"
                >
                  {t('tutorial.skip')}
                </button>
              )}
              <button
                onClick={next}
                className="flex items-center gap-1.5 px-5 py-2 rounded-xl text-xs font-semibold text-white transition-all hover:brightness-110 hover:shadow-lg ml-auto"
                style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}dd)`, boxShadow: `0 4px 15px ${accentColor}30` }}
                data-testid="tutorial-next-btn"
              >
                {current === steps.length - 1 ? t('tutorial.finish') : t('tutorial.next')}
                {current < steps.length - 1 && <ChevronRight className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
